import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Modal,
  Alert,
  Empty,
  Spin,
  Typography,
} from "antd";
import {
  SearchOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import buildColumns from "./columns";
import BlogDetailModal from "./BlogDetailModal";
import KeywordPointsDrawer from "./KeywordPointsDrawer";
import { STORES, DEFAULT_STORE, hashForLabel, resolveBlogUrl } from "./stores";
import { triggerNotification } from "../../common/notify";
import { SHARED_STORE_LS_KEY } from "../common/storeDefaults";

const LS_STORE_KEY = SHARED_STORE_LS_KEY;

const STORE_OPTIONS = STORES.map((s) => ({ value: s.label, label: `${s.label} — ${s.name}` }));
const PUBLISHED_OPTIONS = [
  { value: "", label: "Any" },
  { value: 1, label: "Published" },
  { value: 0, label: "Unpublished" },
];
const DEFAULT_OPTIONS = [
  { value: "", label: "All blogs" },
  { value: 1, label: "Default only" },
];
const TAG_FILTER_OPTIONS = [
  { value: "all",           label: "All Blogs Tagged/Untagged" },
  { value: "with_tags",    label: "Tagged Blogs" },
  { value: "without_tags", label: "Untagged Blogs" },
];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const VALID_TAG_FILTERS = new Set(TAG_FILTER_OPTIONS.map((o) => o.value));

const readInitialFilters = (searchParams) => {
  const lsStore = localStorage.getItem(LS_STORE_KEY);
  const validLabels = STORES.map((s) => s.label);
  const qpStore = searchParams.get("store");
  const store_label = validLabels.includes(qpStore)
    ? qpStore
    : validLabels.includes(lsStore)
    ? lsStore
    : DEFAULT_STORE;

  const parseInt01 = (v) => (v === "0" ? 0 : v === "1" ? 1 : "");
  const rawTag = searchParams.get("btag");

  return {
    store_label,
    search:     searchParams.get("search") || "",
    keyword:    searchParams.get("bkw") || "",
    tag_filter: VALID_TAG_FILTERS.has(rawTag) ? rawTag : "all",
    is_published: parseInt01(searchParams.get("is_published")),
    is_default:   parseInt01(searchParams.get("is_default")),
    page: Math.max(parseInt(searchParams.get("page"), 10) || 1, 1),
    limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("limit"), 10))
      ? parseInt(searchParams.get("limit"), 10)
      : 20,
  };
};

const BlogsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput]   = useState(filters.search);
  const [keywordInput, setKeywordInput] = useState(filters.keyword);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [detailId, setDetailId] = useState(null);
  const [keywordBlog, setKeywordBlog] = useState(null);
  const [togglingDefaultId, setTogglingDefaultId] = useState(null);
  const [tagCountMap, setTagCountMap] = useState({});

  const [syncing, setSyncing] = useState(false);
  const [syncStartedAt, setSyncStartedAt] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(LS_STORE_KEY, filters.store_label);
  }, [filters.store_label]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("store", filters.store_label);
    if (filters.search)  next.set("search", filters.search);
    if (filters.keyword) next.set("bkw", filters.keyword);
    else next.delete("bkw");
    if (filters.tag_filter && filters.tag_filter !== "all")
      next.set("btag", filters.tag_filter);
    else next.delete("btag");
    if (filters.is_published !== "") next.set("is_published", String(filters.is_published));
    if (filters.is_default   !== "") next.set("is_default",   String(filters.is_default));
    next.set("page",  String(filters.page));
    next.set("limit", String(filters.limit));
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.keyword === keywordInput ? prev : { ...prev, keyword: keywordInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [keywordInput]);

const fetchData = useCallback(async () => {
    setFetching(true);
    setLoadError(null);
    try {
      const { items, pagination: pg } = await apiHelpers.fetchBlogs(filters);
      setRows(items);
      setPagination(pg);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, totalPages: 0 });
      triggerNotification({ type: "error", title: "Failed to load blogs", message: mapped.message });
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const resetFilters = () => {
    setSearchInput("");
    setKeywordInput("");
    setFilters({
      store_label:  DEFAULT_STORE,
      search:       "",
      keyword:      "",
      tag_filter:   "all",
      is_published: "",
      is_default:   "",
      page:         1,
      limit:        20,
    });
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  const startPolling = useCallback(
    (storeHash) => {
      stopPolling();
      const pollStartedAt = Date.now();
      pollRef.current = setInterval(async () => {
        if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
          stopPolling();
          setSyncing(false);
          setSyncStartedAt(null);
          triggerNotification({
            type: "warning",
            title: "Sync timed out",
            message: "Sync is taking longer than expected. Please refresh the page to check status.",
          });
          return;
        }
        try {
          const status = await apiHelpers.fetchSyncStatus(storeHash);
          if (!status.running) {
            stopPolling();
            setSyncing(false);
            setSyncStartedAt(null);
            triggerNotification({
              type: "success",
              title: "Sync complete",
              message: "Blogs were refreshed from BigCommerce.",
            });
            fetchData();
          }
        } catch {
          stopPolling();
          setSyncing(false);
        }
      }, 3000);
    },
    [fetchData, stopPolling]
  );

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await apiHelpers.triggerSync(filters.store_label);
      const storeHash = res?.storeHash || hashForLabel(filters.store_label);
      setSyncStartedAt(Date.now());
      startPolling(storeHash);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      setSyncing(false);
      triggerNotification({
        type: mapped.code === "SYNC_IN_PROGRESS" ? "info" : "error",
        title: mapped.code === "SYNC_IN_PROGRESS" ? "Sync already running" : "Sync failed",
        message: mapped.message,
      });
    }
  };

  const handleView = useCallback((record) => setDetailId(record.id), []);

  const handleManageKeywords = useCallback(
    (record) => setKeywordBlog(record),
    []
  );

  const handleOpenBc = useCallback((record) => {
    if (!record.blogUrl) return;
    const fullUrl = resolveBlogUrl(record.blogUrl, {
      storeHash: record.storeHash,
      storeLabel: record.storeLabel,
    });
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  }, []);

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: "Delete blog?",
        icon: <ExclamationCircleOutlined />,
        content: (
          <span>
            Delete <b>{record.title || "this blog"}</b>? This cannot be undone.
          </span>
        ),
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: async () => {
          const snapshot = rows;
          setRows((prev) => prev.filter((r) => r.id !== record.id));
          try {
            await apiHelpers.deleteBlog(record.id);
            triggerNotification({
              type: "success",
              title: "Deleted",
              message: `Blog "${record.title}" was deleted.`,
            });
            fetchData();
          } catch (err) {
            setRows(snapshot);
            const mapped = apiHelpers.mapError(err);
            triggerNotification({
              type: "error",
              title: "Delete failed",
              message: mapped.message,
            });
          }
        },
      });
    },
    [rows, fetchData]
  );

  const handleToggleDefault = useCallback(
    async (record, nextValue) => {
      if (togglingDefaultId !== null) return; // one toggle at a time
      const prevValue = Number(record.isDefault) === 1 ? 1 : 0;
      if (prevValue === nextValue) return;

      setTogglingDefaultId(record.id);
      const snapshot = rows;
      // Multiple defaults are allowed (up to the server-configured cap), so only this row
      // changes — other defaults are left intact.
      setRows((prev) =>
        prev.map((r) => (r.id === record.id ? { ...r, isDefault: nextValue } : r))
      );
      setKeywordBlog((prev) =>
        prev && prev.id === record.id ? { ...prev, isDefault: nextValue } : prev
      );
      try {
        const updated = await apiHelpers.setBlogDefault(record.id, nextValue);
        if (updated) {
          const confirmed = Number(updated.isDefault) === 1 ? 1 : 0;
          setRows((prev) =>
            prev.map((r) => (r.id === record.id ? { ...r, isDefault: confirmed } : r))
          );
          setKeywordBlog((prev) =>
            prev && prev.id === record.id ? { ...prev, isDefault: confirmed } : prev
          );
        }
        triggerNotification({
          type: "success",
          title: nextValue === 1 ? "Default set" : "Default removed",
          message:
            nextValue === 1
              ? `"${record.title || "Blog"}" is now a default for ${filters.store_label}.`
              : `"${record.title || "Blog"}" is no longer a default.`,
        });
      } catch (err) {
        setRows(snapshot); // rollback the optimistic change
        setKeywordBlog((prev) =>
          prev && prev.id === record.id
            ? { ...prev, isDefault: Number(record.isDefault) === 1 ? 1 : 0 }
            : prev
        );
        const mapped = apiHelpers.mapError(err);
        const isLimit = mapped.code === "DEFAULT_LIMIT_REACHED";
        triggerNotification({
          type: isLimit || mapped.code === "NOT_FOUND" ? "warning" : "error",
          title: isLimit ? "Default limit reached" : "Update failed",
          message: mapped.message,
        });
      } finally {
        setTogglingDefaultId(null);
      }
    },
    [togglingDefaultId, rows, filters.store_label]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        onView: handleView,
        onOpenBc: handleOpenBc,
        onDelete: handleDelete,
        onManageKeywords: handleManageKeywords,
        tagCountMap,
        page: filters.page,
        limit: filters.limit,
      }),
    [handleView, handleOpenBc, handleDelete, handleManageKeywords, tagCountMap, filters.page, filters.limit]
  );

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Select
              value={filters.store_label}
              onChange={(v) => updateFilter({ store_label: v })}
              options={STORE_OPTIONS}
              style={{ width: 200 }}
            />
            <Input
              allowClear
              placeholder="Search title / author"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 220 }}
            />
            <Input
              allowClear
              placeholder="Has tag"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              style={{ width: 150 }}
            />
            <Select
              value={filters.tag_filter}
              onChange={(v) => updateFilter({ tag_filter: v })}
              options={TAG_FILTER_OPTIONS}
              style={{ width: 160 }}
            />
            <Select
              value={filters.is_published}
              onChange={(v) => updateFilter({ is_published: v })}
              options={PUBLISHED_OPTIONS}
              style={{ width: 135 }}
              placeholder="Published"
            />
            <Select
              value={filters.is_default}
              onChange={(v) => updateFilter({ is_default: v })}
              options={DEFAULT_OPTIONS}
              style={{ width: 140 }}
              placeholder="All blogs"
            />
            <Select
              value={filters.limit}
              onChange={(v) => updateFilter({ limit: v })}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              style={{ width: 120 }}
            />
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          </Space>
          <Button
            type="primary"
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync Blog From BC"}
          </Button>
        </Space>
      </div>

      {syncing && (
        <Alert
          type="info"
          showIcon
          message={
            <Space>
              <Spin size="small" />
              <span>
                Sync running for {filters.store_label}
                {syncStartedAt && ` — ${Math.round((Date.now() - syncStartedAt) / 1000)}s elapsed`}
              </span>
            </Space>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div className="TableBox" style={{ maxHeight: 700, overflow: "auto", opacity: fetching && !showFullPageLoader ? 0.6 : 1, transition: "opacity 0.15s" }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No blogs found for <b>{filters.store_label}</b>. Try changing filters or syncing from BC.
                  </span>
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of {pagination.totalPages || 1}
              </Typography.Text>
            ),
            onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
          bordered
          scroll={{ x: 1100, y: 500 }}
        />
      </div>

      <BlogDetailModal
        open={!!detailId}
        blogId={detailId}
        onClose={() => setDetailId(null)}
      />

      <KeywordPointsDrawer
        open={!!keywordBlog}
        blog={keywordBlog}
        onClose={() => setKeywordBlog(null)}
        onToggleDefault={handleToggleDefault}
        togglingDefaultId={togglingDefaultId}
        onCountChange={(blogId, count) =>
          setTagCountMap((prev) =>
            prev[blogId] === count ? prev : { ...prev, [blogId]: count }
          )
        }
      />
    </>
  );
};

export default BlogsTable;
